/**
 * NCR Edit Page
 *
 * Refactored to use modular sub-components for improved maintainability.
 * All state management is handled by the useNCREdit hook.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  ArrowLeft, Loader2, Package, CreditCard, FileWarning,
} from 'lucide-react';
import {
  useNCREdit,
  Template1ProgressBar,
  Template2ProgressBar,
  NCRDetailTab,
  RootCauseTab,
  CorrectiveActionTab,
  DocumentsSection,
  CommentsSection,
  ActionLogSection,
  CloseNCRDialog,
} from './ncr-edit';
import { CollectionWorkflowTab } from './ncr-edit/CollectionWorkflowTab';
import { CreditWorkflowTab } from './ncr-edit/CreditWorkflowTab';
import { platformSettingsAPI, QualityTabsVisibility, NonConformanceReport } from '../lib/api';

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'destructive';

/**
 * Map the collection sub-process status to the badge shown next to the
 * Collection tab so the tab label tracks the breadcrumb stepper inside.
 */
function collectionBadgeFor(ncr: NonConformanceReport): { label: string; variant: BadgeVariant } {
  if (ncr.collection_completed) return { label: 'Done', variant: 'default' };
  const status = ncr.collection_process?.status;
  if (!status) return { label: 'Not Started', variant: 'secondary' };
  switch (status) {
    case 'requested': return { label: 'Requested', variant: 'secondary' };
    case 'pending':   return { label: 'Pending', variant: 'secondary' };
    case 'confirmed': return { label: 'Confirmed', variant: 'outline' };
    case 'completed': return { label: 'Done', variant: 'default' };
    default:          return { label: 'Pending', variant: 'secondary' };
  }
}

/**
 * Map the credit sub-process status to the badge shown next to the
 * Credit tab so the tab label tracks the breadcrumb stepper inside.
 */
function creditBadgeFor(ncr: NonConformanceReport): { label: string; variant: BadgeVariant } {
  if (ncr.credit_completed) return { label: 'Done', variant: 'default' };
  const status = ncr.credit_process?.status;
  if (!status) return { label: 'Not Started', variant: 'secondary' };
  switch (status) {
    case 'requested':    return { label: 'Requested', variant: 'secondary' };
    case 'under_review': return { label: 'Under Review', variant: 'secondary' };
    case 'approved':     return { label: 'Approved', variant: 'outline' };
    case 'rejected':     return { label: 'Rejected', variant: 'destructive' };
    case 'processed':    return { label: 'Done', variant: 'default' };
    default:             return { label: 'Pending', variant: 'secondary' };
  }
}


export default function NCREdit() {
  const navigate = useNavigate();
  const {
    // Data
    ncr,
    documents,
    comments,
    actionLogs,
    formData,

    // UI states
    loading,
    saving,
    currentStep,
    ncrTemplate,
    newComment,
    closeDialogOpen,

    // Permissions
    isCustomer,
    canEditStep1,
    canEditStep2,
    canEditStep3,

    // Customer users
    customerUsers,

    // Classification options
    sourcesOfRejection,
    faultGroups,
    faultCategories,

    // Setters
    setNewComment,
    setCloseDialogOpen,
    updateFormData,

    // Actions
    refreshData,
    goBack,
    handleSave,
    handleSubmitDetail,
    handleSubmitRCA,
    handleSubmitCAR,
    handleAddComment,
    handleClose,
    getTemplate2Steps,

    // Multi-step workflow
    handleInitiateCollection,
    handleUpdateCollectionStatus,
    handleInitiateCredit,
    handleUpdateCreditStatus,

    // Computed
    canClose,
  } = useNCREdit();

  // Tab visibility check
  const [tabVisibility, setTabVisibility] = useState<QualityTabsVisibility>({
    ncrs: true, concessions: true, approval_register: true,
  });
  const [tabSettingsLoaded, setTabSettingsLoaded] = useState(false);

  // Check if NCRs tab is enabled
  useEffect(() => {
    platformSettingsAPI.getDisplaySettings()
      .then(res => {
        if (res.data?.quality_tabs) {
          setTabVisibility(prev => ({ ...prev, ...res.data.quality_tabs }));
        }
      })
      .catch(() => { /* use defaults */ })
      .finally(() => setTabSettingsLoaded(true));
  }, []);

  // Redirect if NCRs tab is disabled
  useEffect(() => {
    if (!tabSettingsLoaded) return;
    if (!tabVisibility.ncrs) {
      navigate('/quality', { replace: true });
    }
  }, [tabSettingsLoaded, tabVisibility.ncrs, navigate]);

  // Show loading while checking tab settings or loading NCR
  if (!tabSettingsLoaded || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // If NCRs tab is disabled, show access denied (redirect should have happened)
  if (!tabVisibility.ncrs) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">This section is not available. Please contact your administrator.</p>
        </div>
      </div>
    );
  }

  if (!ncr) {
    return (
      <div className="p-6">
        <div className="text-center">NCR not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={goBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{ncr.ncr_number}</h1>
            <p className="text-muted-foreground">NCR ID: {ncr.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={ncr.status === 'closed' ? 'default' : 'secondary'}>
            {ncr.status_display}
          </Badge>
        </div>
      </div>

      {/* Progress Steps */}
      {ncrTemplate === 2 ? (
        <Template2ProgressBar steps={getTemplate2Steps()} />
      ) : (
        <Template1ProgressBar currentStep={currentStep} />
      )}

      {/* NCR Details - Always visible at top */}
      <NCRDetailTab
        ncr={ncr}
        formData={formData}
        canEdit={canEditStep1}
        saving={saving}
        customerUsers={customerUsers}
        sourcesOfRejection={sourcesOfRejection}
        faultGroups={faultGroups}
        faultCategories={faultCategories}
        onUpdateFormData={updateFormData}
        onSave={handleSave}
        onSubmitDetail={handleSubmitDetail}
        onCloseNo8D={() => setCloseDialogOpen(true)}
      />

      {/* Workflow Tabs: 8D Report, Collection, Credit */}
      <Tabs defaultValue="8d_report" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="8d_report" className="flex items-center gap-2">
            <FileWarning className="h-4 w-4" />
            8D Report
          </TabsTrigger>
          <TabsTrigger value="collection" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Collection
            {ncr.is_collection_required && (() => {
              const b = collectionBadgeFor(ncr);
              return (
                <Badge variant={b.variant} className="ml-1 text-[10px] px-1.5 py-0">
                  {b.label}
                </Badge>
              );
            })()}
          </TabsTrigger>
          <TabsTrigger value="credit" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Credit
            {ncr.is_credit_required && (() => {
              const b = creditBadgeFor(ncr);
              return (
                <Badge variant={b.variant} className="ml-1 text-[10px] px-1.5 py-0">
                  {b.label}
                </Badge>
              );
            })()}
          </TabsTrigger>
        </TabsList>

        {/* 8D Report Tab — Root Cause + Corrective Action exposed as nested
            sub-tabs (customer request) instead of stacked sections.
            Default sub-tab follows the NCR workflow stage so the user lands
            on the section they're most likely to be working on:
              draft / ca_submitted → Root Cause (RCA in progress)
              car_submitted / closed → Corrective Action (RCA already done) */}
        <TabsContent value="8d_report" className="space-y-4">
          <Tabs
            defaultValue={
              ['car_submitted', 'closed'].includes(ncr.status) ? 'corrective_action' : 'root_cause'
            }
            className="space-y-4"
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="root_cause">Root Cause Analysis</TabsTrigger>
              <TabsTrigger value="corrective_action">Corrective Action Report</TabsTrigger>
            </TabsList>
            <TabsContent value="root_cause">
              <RootCauseTab
                formData={formData}
                canEdit={canEditStep2}
                saving={saving}
                onUpdateFormData={updateFormData}
                onSave={handleSave}
                onSubmitRCA={handleSubmitRCA}
              />
            </TabsContent>
            <TabsContent value="corrective_action">
              <CorrectiveActionTab
                formData={formData}
                canEdit={canEditStep3}
                saving={saving}
                onUpdateFormData={updateFormData}
                onSave={handleSave}
                onSubmitCAR={handleSubmitCAR}
              />
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Collection Tab */}
        <TabsContent value="collection">
          <CollectionWorkflowTab
            ncr={ncr}
            isCustomer={isCustomer}
            isSupplier={!isCustomer}
            saving={saving}
            onInitiate={handleInitiateCollection}
            onUpdateStatus={handleUpdateCollectionStatus}
          />
        </TabsContent>

        {/* Credit Tab */}
        <TabsContent value="credit">
          <CreditWorkflowTab
            ncr={ncr}
            isCustomer={isCustomer}
            isSupplier={!isCustomer}
            saving={saving}
            onInitiate={handleInitiateCredit}
            onUpdateStatus={handleUpdateCreditStatus}
          />
        </TabsContent>
      </Tabs>

      {/* Documents */}
      <DocumentsSection ncrId={ncr.id} documents={documents} onDocumentsChanged={refreshData} />

      {/* Communications */}
      <CommentsSection
        comments={comments}
        newComment={newComment}
        onNewCommentChange={setNewComment}
        onAddComment={handleAddComment}
      />

      {/* Action Log */}
      <ActionLogSection actionLogs={actionLogs} />

      {/* Close NCR Button - only when all required steps done */}
      {isCustomer && ncr.status === 'car_submitted' && canClose && (
        <div className="flex justify-end">
          <Button onClick={() => setCloseDialogOpen(true)} size="lg">
            Close This NCR
          </Button>
        </div>
      )}

      {/* Close NCR Confirmation Dialog */}
      <CloseNCRDialog
        open={closeDialogOpen}
        onOpenChange={setCloseDialogOpen}
        onClose={handleClose}
      />
    </div>
  );
}
